import { Button } from 'tio-ui/components/buttons';
import { dropTask } from 'ember-concurrency';
import { Input, Switch } from 'tio-ui/components/forms';
import { on } from '@ember/modifier';
import { PhLock } from 'ember-phosphor-icons';
import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import * as v from 'valibot';
import Component from '@glimmer/component';
import formatPhone from 'tio-common/helpers/format-phone';
import type EmployeeModel from 'tio-common/models/employee';
import type PersonModel from 'tio-common/models/person';
import type Owner from '@ember/owner';
import type PhoneNumber from 'tio-common/models/phone-number';
import type SessionContextService from '../../../../services/session-context';
import type SessionService from '../../../../services/session';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';

export interface SecuritySettingsSignature {
  Args: {
    employee?: EmployeeModel;
    familyPerson?: PersonModel;
  };
}

export default class SecuritySettings extends Component<SecuritySettingsSignature> {
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;

  @tracked isOpen = true;
  @tracked isLoading = false;
  @tracked error = null;
  @tracked isEditingContact = false;
  @tracked phoneValue!: string;

  constructor(owner: Owner, args: SecuritySettingsSignature['Args']) {
    super(owner, args);
    this.setInitialPhoneValue();
  }

  get person() {
    return this.args.familyPerson ?? this.args.employee?.person;
  }

  get primaryPhoneNumber() {
    return this.args.employee?.primaryPhoneNumber ?? this.person?.primaryPhoneNumber;
  }

  setInitialPhoneValue() {
    this.phoneValue = this.primaryPhoneNumber?.number?.replace(/[^0-9]/g, '') || '';
  }

  get login() {
    return this.person?.user?.logins?.find((login) => login.isActive);
  }

  get userName() {
    return this.login ? this.login.userName : '';
  }

  get isMfaEnabled() {
    return this.login?.twoFactorEnabled ?? false;
  }

  get mfaPhoneNumber() {
    return (
      this.login?.twoFactorPhoneNumber || this.primaryPhoneNumber?.number || this.phoneValue || ''
    );
  }

  get isMfaToggleDisabled() {
    return this.updateMfaSettings.isRunning || (!this.isPhoneNumberValid && !this.isMfaEnabled);
  }

  get showMfaPhonePrompt() {
    return !this.isPhoneNumberValid && !this.isMfaEnabled;
  }

  get showMfaPhoneNumber() {
    return !this.showMfaPhonePrompt && !!this.mfaPhoneNumber;
  }

  toggleExpand = () => {
    this.isOpen = !this.isOpen;
  };

  toggleContactEdit = () => {
    this.isEditingContact = !this.isEditingContact;
    this.setInitialPhoneValue();
  };

  setPhoneValue = (value: string, event?: InputEvent) => {
    // Remove any non-numeric characters from the input
    const target = event?.target as HTMLInputElement;
    if (isNaN(Number(value))) {
      target.value = target.value.replace(/[^0-9]/g, '');
      value = target.value;
    }

    // Limit the input to 10 digits (or 11 if it starts with "1")
    if ((value.length > 10 && !value.startsWith('1')) || value.length > 11) {
      const length = value.startsWith('1') ? 11 : 10;
      target.value = target.value.slice(0, length);
      value = target.value;
    }

    this.phoneValue = value;
  };

  get phoneSchema() {
    return v.pipe(v.string(), v.regex(/^\+?[1-9]\d{9,14}$/, 'Invalid phone number format'));
  }

  get isPhoneNumberValid() {
    const res = v.safeParse(this.phoneSchema, this.phoneValue);
    return !!res.success;
  }

  get isSubmitDisabled() {
    return !this.isPhoneNumberValid || this.phoneValue === this.primaryPhoneNumber?.number;
  }

  savePhoneNumber = dropTask(async () => {
    if (this.primaryPhoneNumber) {
      this.primaryPhoneNumber.number = this.phoneValue;
      await this.primaryPhoneNumber.save({
        adapterOptions: { changelog: 'updated by employee' },
      });
    } else if (this.person?.user) {
      const newPhoneNumber = this.store.createRecord<PhoneNumber>('phone-number', {
        contactType: 'TELEPHONE',
        number: this.phoneValue,
        phoneNumberType: 'MOBILE',
        user: this.person.user,
      });
      await newPhoneNumber.save({ adapterOptions: { changelog: 'added by employee' } });
    }

    await this.updateMfaPhoneNumberIfEnabled();

    if (this.args.employee) {
      await this.args.employee.reload({ include: 'phone-numbers' });
    } else if (this.args.familyPerson) {
      await this.args.familyPerson.reload({ include: 'phone-numbers' });
    }
    this.toggleContactEdit();
  });

  updateMfaPhoneNumberIfEnabled = async () => {
    if (!this.login || !this.isMfaEnabled || !this.isPhoneNumberValid) {
      return;
    }

    this.login.twoFactorPhoneNumber = this.phoneValue;
    await this.login.save();
  };

  updateMfaSettings = dropTask(async (isSelected: boolean) => {
    if (!this.login) {
      return;
    }

    if (isSelected && !this.isPhoneNumberValid) {
      return;
    }

    this.login.twoFactorEnabled = isSelected;
    if (isSelected) {
      this.login.twoFactorPhoneNumber = this.phoneValue;
    }

    await this.login.save();
  });

  <template>
    <Section>
      <:header>{{t "profile.security_settings"}}</:header>
      <:body>
        <div class="flex flex-col">
          <div class="flex flex-row mx-8 my-4">
            <div class="self-center w-1/4 font-semibold text-gray-700">
              {{t "profile.personal_info"}}
            </div>
            <div class="flex flex-col w-1/5">
              <span class="text-xs">{{t "login.first_name"}}</span>
              <span class="ml-4 text-xl my-4">{{this.person.firstName}}</span>
            </div>
            <div class="flex flex-col w-1/5">
              <span class="text-xs">{{t "login.last_name"}}</span>
              <span class="ml-4 text-xl my-4">{{this.person.lastName}}</span>
            </div>
            <div class="flex-col ml-auto w-1/6 text-gray-500 grid justify-items-center">
              <PhLock @size="24" @weight="fill" />
              <span class="text-xs">{{t "profile.to_edit_contact_support"}}</span>
            </div>
          </div>
          <hr />
          <div class="flex flex-row mx-8 my-4">
            <div class="self-center w-1/4 font-semibold text-gray-700">
              {{t "profile.username_login"}}
            </div>
            <div class="flex flex-col w-1/5">
              <span class="text-xs">{{t "profile.username"}}</span>
              <span class="text-xl my-4 ml-4">{{this.userName}}</span>
            </div>
            <div class="flex-col ml-auto w-1/6 text-gray-500 grid justify-items-center">
              <PhLock @size="24" @weight="fill" />
              <span class="text-xs">{{t "profile.to_edit_contact_support"}}</span>
            </div>
          </div>
          <hr />
          <div class="flex flex-row mx-8 my-4">
            <div class="self-center w-1/4 font-semibold text-gray-700">
              {{t "profile.contact_info"}}
            </div>
            <div class="flex flex-col">
              {{#if this.isEditingContact}}
                <Input
                  @value={{formatPhone this.phoneValue}}
                  @onChange={{this.setPhoneValue}}
                  @label={{t "profile.mobile_phone"}}
                  data-test-phone-input
                />
              {{else if this.phoneValue}}
                <span class="text-xl my-4 ml-4">
                  {{formatPhone this.phoneValue}}
                </span>
              {{else}}
                <span class="text-xs my-4 ml-4 text-red-500">
                  {{t "profile.no_phone"}}
                </span>
              {{/if}}
            </div>
            <div class="flex flex-row justify-end self-center ml-auto w-1/6 gap-x-2">
              {{#if this.isEditingContact}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.savePhoneNumber.perform}}
                  disabled={{this.isSubmitDisabled}}
                  data-test-contact-save
                >
                  {{t "common.save"}}
                </Button>
                <Button
                  @appearance="outlined"
                  @intent="danger"
                  {{on "click" this.toggleContactEdit}}
                  data-test-contact-cancel
                >
                  {{t "common.cancel"}}
                </Button>
              {{else}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.toggleContactEdit}}
                  class="self-auto"
                  data-test-contact-edit
                >
                  {{t "common.edit"}}
                </Button>
              {{/if}}
            </div>
          </div>
          <hr />
          <div class="flex flex-row mx-8 my-4" data-test-mfa-section>
            <div class="self-center w-1/4 font-semibold text-gray-700">
              {{t "profile.mfa.title"}}
            </div>
            <div class="flex flex-col w-2/5">
              <span class="text-xs">{{t "profile.mfa.status"}}</span>
              <span class="text-xl my-4 ml-4" data-test-mfa-status>
                {{if
                  this.isMfaEnabled
                  (t "profile.mfa.status_enabled")
                  (t "profile.mfa.status_disabled")
                }}
              </span>
              {{#if this.showMfaPhonePrompt}}
                <span class="text-xs text-red-500 ml-4" data-test-mfa-phone-required>
                  {{t "profile.mfa.phone_required"}}
                </span>
              {{else if this.showMfaPhoneNumber}}
                <span class="text-xs text-gray-500 ml-4" data-test-mfa-phone-number>
                  {{t "profile.mfa.sms_number"}}
                  {{formatPhone this.mfaPhoneNumber}}
                </span>
              {{/if}}
            </div>
            <div class="flex flex-row justify-end self-center ml-auto w-1/6 gap-x-2">
              <div data-test-mfa-toggle>
                <Switch
                  @isSelected={{this.isMfaEnabled}}
                  @onChange={{this.updateMfaSettings.perform}}
                  @isDisabled={{this.isMfaToggleDisabled}}
                  @label={{t "profile.mfa.toggle_label"}}
                />
              </div>
            </div>
          </div>
        </div>
      </:body>
    </Section>
  </template>
}
